/**
 * Site-selection context and its hook.
 *
 * Separate from the provider so each module exports one kind of thing, which is what
 * React Fast Refresh needs to hot-reload either of them.
 */
import { createContext, useContext } from 'react';
import type { SiteListItem } from '@/lib/api/sites';

export interface SiteContextValue {
  /** Every site the caller may see. Fleet lists, maps and archetype lookups read this. */
  sites: SiteListItem[];
  /**
   * The sites the HEADER SELECTOR offers: every site that has reported at least once, plus
   * whichever site is currently selected even if it has not. Test fixtures and sites that
   * are mid-commissioning have never published, so they do not appear as choices here; they
   * remain on the Sites page and are selectable from it, and a site onboarded a moment ago is
   * selected explicitly by the onboarding flow, which is what the "plus the selected" clause
   * exists for.
   */
  selectable: SiteListItem[];
  loading: boolean;
  /**
   * The site list could not be loaded. Distinct from `sites.length === 0`, which the selector
   * used to render as "No sites" — a fetch failure presented as an empty fleet.
   */
  error: boolean;
  selectedId: string | null;
  selected: SiteListItem | null;
  select: (id: string) => void;
}

export const SiteContext = createContext<SiteContextValue | null>(null);

export function useSiteContext(): SiteContextValue {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error('useSiteContext must be used inside <SiteProvider>');
  return ctx;
}

/**
 * How a screen relates to the header's site selection. Declared on every nav item so the
 * selector can say, on every page, whether the site it names is what the page is showing.
 *
 *   site   the page follows the header site (it may offer one labelled fleet escape)
 *   fleet  the page is a fleet view by nature — the site list, the integration queue
 *   none   the page has no site meaning — users, audit, your account
 */
export type SiteScope = 'site' | 'fleet' | 'none';
